import type { Edition } from '@zyx/shared';

/** `2:34:29`, or `21:21` under an hour. */
export function formatRuntime(seconds: number | undefined): string | undefined {
  if (seconds === undefined || seconds <= 0) {
    return undefined;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = Math.floor(seconds % 60);
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}

/** "3 days ago", the way a feed reads. */
export function formatAgo(ms: number | undefined, now: number = Date.now()): string | undefined {
  if (ms === undefined) {
    return undefined;
  }
  const seconds = Math.max(0, Math.round((now - ms) / 1000));
  const steps: ReadonlyArray<readonly [number, string]> = [
    [365 * 86400, 'year'],
    [30 * 86400, 'month'],
    [7 * 86400, 'week'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
  ];
  for (const [size, unit] of steps) {
    if (seconds >= size) {
      const count = Math.floor(seconds / size);
      return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
    }
  }
  return 'just now';
}

/** `15K`, `1.2M`. */
export function formatCount(count: number | undefined): string | undefined {
  if (count === undefined) {
    return undefined;
  }
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(count >= 10_000_000 ? 0 : 1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(count >= 10_000 ? 0 : 1)}K`;
  }
  return String(count);
}

const EDITION_LABELS: Readonly<Record<Edition, string>> = {
  censored: 'Censored',
  uncensored: 'Uncensored',
  uncensored_leaked: 'Leaked',
  other: 'Other',
};

export function editionLabel(edition: Edition): string {
  return EDITION_LABELS[edition];
}

/** How far through a movie the viewer is, 0–1. */
export function progressFraction(progress: { readonly positionS: number; readonly durationS: number } | undefined): number | undefined {
  if (progress === undefined || progress.durationS <= 0) {
    return undefined;
  }
  return Math.min(1, Math.max(0, progress.positionS / progress.durationS));
}

/** `1280x720` → `720p`; a stream with one unnamed quality is `Original`. */
export function qualityLabel(resolution: string): string {
  const height = /^\d+x(\d+)$/.exec(resolution)?.[1];
  return height === undefined ? (resolution === 'source' ? 'Original' : resolution) : `${height}p`;
}
